/* eslint-disable no-underscore-dangle */
import omit from 'lodash.omit';

import { createFiltersFromArray, getFilterObject } from '../utils';
import { withDataRequest } from '../shared/higherOrderReducers/withDataRequest/withDataRequest.reducer';
import { getReduxCachePrefix, DEALER_FEATURES } from '../constants';

import { checkBoxFilters, rangeFilters, radioFilters } from './filters';
import * as ActionTypes from './constants';

/**
 * @typedef {import('./../utils').FilterCategory} FilterCategory
 * @property {FilterCategory|FilterCategory[]} payload
 */

/**
 * @typedef ListingsFiltersState
 * @property {string[]} dealer - an array of dealer ids
 * @property {FilterCategory[]} page
 * @property {FilterCategory[]} sortyBy
 *
 */
/**
 *
 * @function listingsFiltersDefaultState
 * @returns {ListingsFiltersState}
 */
export const listingsFiltersDefaultState = () => ({
  dealer: [], // TODO: rename to dealerIds
  partialExternalId: [],
  page: [getFilterObject({ category: 'page', value: 0 })],
  sortBy: [
    getFilterObject({
      category: 'sortBy',
      value: 'createdAt',
      direction: 'desc',
    }),
  ],
});

/**
 * @function listingsFilters
 * @param {ListingsFiltersState} state
 * @param {{type: string, payload: FilterCategory|FilterCategory[]}} action
 */
export function listingsFilters(state = listingsFiltersDefaultState(), action) {
  const { type, payload } = action;
  switch (type) {
    case ActionTypes.SET_FILTERS:
      return {
        ...state,
        ...createFiltersFromArray([payload || []].flat(), state, {
          checkBox: checkBoxFilters,
          range: rangeFilters,
          radio: radioFilters,
        }),
      };
    case ActionTypes.CLEAR_DEALER_FILTERS:
      return {
        ...state,
        dealer: [],
      };
    default:
      return state;
  }
}

export const defaultState = () => {
  return {
    pending: false,
    dealerListings: [],
    dealerListingsCount: null,
    error: null,
  };
};

function listingsReducer(state = defaultState(), action) {
  const {
    type,
    payload,
    error,
    listingsKeyValuePair,
    cachedListingsData,
  } = action;

  if (type === ActionTypes.GET_DEALER_LISTINGS_REQUEST) {
    return {
      ...state,
      pending: true,
    };
  }

  if (type === ActionTypes.DEALER_LISTING_TOGGLE_SUCCESS) {
    const newListings = state.dealerListings.map(listing => {
      if (listing.id === payload.id) {
        return payload;
      }
      return listing;
    });

    return {
      ...state,
      dealerListings: newListings,
    };
  }

  if (type === ActionTypes.GET_DEALER_LISTINGS_SUCCESS) {
    return {
      ...state,
      dealerListings: payload._embedded?.listings || [],
      pending: false,
      error: null,
      dealerListingsCount: payload.page.totalElements,
    };
  }

  if (type === ActionTypes.GET_DEALER_LISTINGS_FAILURE) {
    return {
      ...state,
      error,
      pending: false,
      dealerListingsCount: 0,
      dealerListings: [],
    };
  }

  if (type === ActionTypes.CACHE_DEALER_LISTINGS) {
    return {
      ...state,
      ...listingsKeyValuePair,
    };
  }

  if (type === ActionTypes.APPLY_CACHED_DEALER_LISTINGS) {
    const { dealerListings, dealerListingsCount } = cachedListingsData;
    return {
      ...state,
      dealerListings,
      dealerListingsCount,
    };
  }

  /**
   * @description
   * CLEAR_LISTINGS_CACHE
   *
   * this will clear the cache if something
   * in the listing has changed from the backend
   * (e.g. listing was paused)
   */
  if (type === ActionTypes.CLEAR_LISTINGS_CACHE) {
    // find cached state keys
    const stateCacheKeys = Object.keys(state).filter(key =>
      key.includes(getReduxCachePrefix(DEALER_FEATURES.LISTINGS)),
    );
    // return new object copy with omitted keys
    return omit(state, stateCacheKeys);
  }

  if (type === ActionTypes.RESET_LISTINGS) {
    return defaultState();
  }

  return state;
}

export const LISTINGS_REDUCER_NAME = 'Listings';
export const listings = withDataRequest(listingsReducer, LISTINGS_REDUCER_NAME);
